import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { useLoadLineItemsTotals } from '@rematter/modules/trade/hooks/useLoadLineItemsTotals';
import { MeasuredValue, Money } from '@rematter/ui-web';

import {
  DeleteButtonCell,
  EstimatedPriceFieldCell,
  MaterialCell,
  NetQuantityFieldCell,
  PricingUnitFieldCell,
  UnitPriceFieldCell,
} from './fields';
import { LineItemErrors } from './types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormLineItem, WeightUnitType } from '~/modules/trade/types/load';
import {
  FooterCell,
  HeaderCell,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
} from '~/shared/components/line-items';

const GRID_TEMPLATE =
  'grid-cols-[minmax(16rem,2fr)_1fr_100px_1fr_2fr_var(--size-input)]';

export interface PurchaseOrderMaterialsTableProps {
  lineItems: LoadFormLineItem[];
  onUpdateLineItem: (id: string, updates: Partial<LoadFormLineItem>) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem?: () => void;
  disabled?: boolean;
  hideAddButton?: boolean;
  isLoading?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  errors?: Record<string, LineItemErrors>;
}

export const PurchaseOrderMaterialsTable: React.FC<
  PurchaseOrderMaterialsTableProps
> = ({
  lineItems,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
  disabled = false,
  hideAddButton = false,
  isLoading = false,
  weightUnit = 'price',
  facilityDefaultUnit,
  errors,
}) => {
  const t = useTranslations();

  const totals = useLoadLineItemsTotals({
    lineItems,
    facilityDefaultUnit: facilityDefaultUnit ?? enumUnit.Pounds,
    weightUnit,
  });

  const isEmpty = lineItems.length === 0;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={hideAddButton}
      disabled={disabled}
      isLoading={isLoading}
      addButtonLabel={t.itemTable.actions.addMaterial}
      className="@PurchaseOrderMaterialsTable"
    >
      <LineItemHeader gridTemplate={GRID_TEMPLATE}>
        <HeaderCell className="justify-start">
          {t.itemTable.purchaseOrderHeader.material}
        </HeaderCell>
        <HeaderCell>{t.itemTable.purchaseOrderHeader.netWeight}</HeaderCell>
        <HeaderCell>{t.itemTable.purchaseOrderHeader.unitPrice}</HeaderCell>
        <HeaderCell>{t.itemTable.purchaseOrderHeader.pricingUnit}</HeaderCell>
        <HeaderCell>
          {t.itemTable.purchaseOrderHeader.estimatedPrice}
        </HeaderCell>
        <HeaderCell />
      </LineItemHeader>

      {lineItems.map((lineItem) => (
        <LineItemRow key={lineItem.id} gridTemplate={GRID_TEMPLATE}>
          <MaterialCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disableMaterialSelection={disabled}
            errors={errors?.[lineItem.id]}
          />
          <NetQuantityFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            errors={errors?.[lineItem.id]}
          />
          <UnitPriceFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
            errors={errors?.[lineItem.id]}
          />
          <PricingUnitFieldCell
            lineItem={lineItem}
            onUpdateLineItem={onUpdateLineItem}
            disabled={disabled}
          />
          <EstimatedPriceFieldCell lineItem={lineItem} disabled />
          <DeleteButtonCell
            lineItem={lineItem}
            onDeleteLineItem={onDeleteLineItem}
            disabled={disabled}
          />
        </LineItemRow>
      ))}

      {!isEmpty && (
        <LineItemFooter gridTemplate={GRID_TEMPLATE}>
          <FooterCell className="font-bold text-neutral">
            {totals.itemsCount}{' '}
            {t.itemTable.salesOrderLoadFooter.material(totals.itemsCount)}
          </FooterCell>
          <FooterCell className="flex-col items-end gap-1 font-bold">
            {!!totals.requestedWeight && (
              <div>
                <MeasuredValue
                  value={totals.requestedWeight.value}
                  unit={totals.requestedWeight.unit}
                />
              </div>
            )}
            {!!totals.requestedEachCount && (
              <div>
                <MeasuredValue
                  value={totals.requestedEachCount}
                  unit={enumUnit.Each}
                />
              </div>
            )}
          </FooterCell>
          <FooterCell />
          <FooterCell />
          <FooterCell className="justify-between font-bold">
            <Money amount={totals.totalEstimatedPrice} />
          </FooterCell>
          <FooterCell />
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
